import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withTranslation, Trans } from 'react-i18next';
import ToggleButton from '../../../components/buttons/toggle_button.jsx';
import ExtendedIo from '../controls/extended_io.jsx';
import * as styles from "../tanks.module.scss";
import * as FileSaver from 'file-saver';
import { getEmptyTank } from '../../../constants/tanks_constants.js';
import tankService from '../../../services/tanksService.js';
import appService from '../../../services/appService.js';
import { selectAppFieldsAccess, selectDeviceTypes, selectIoActionsTypes, selectIoTypes } from '../../../state/selectors/app.js';
import { useHistory, useRouteMatch } from 'react-router';
import { createStructuredSelector } from 'reselect';
import { selectAreas } from '../../../state/selectors/tanks.js';
import { AccessLevel, AppFields } from '../../../constants/appAccess.js';
import { utils } from '../../../utils/utils.js';

const emptyIo = {
    deviceAdditionalIoId: 0,
    deviceImei: "",
    dataType: "int",
    ioTypeId: 1,
    ioAddress: "",
    defaultValue: "",
    offset: 0,
    ratio: 1,
    dataDelta: 0,
    ioDataActionId: 0,
    ioDataActionParam: ""
};

const mapStateToProps = () => {
    return createStructuredSelector({
        areas: selectAreas(),
        deviceTypes: selectDeviceTypes(),
        ioActionTypes: selectIoActionsTypes(),
        ioTypes: selectIoTypes(),
        fieldsAccess: selectAppFieldsAccess()
    });
}

const EditTank = (props) => {
    const { areas, deviceTypes, ioActionTypes, ioTypes, fieldsAccess, i18n } = props;
    const [item, setItem] = useState(getEmptyTank());
    const [graduationFile, setGraduationFile] = useState(null);
    const [selectedIoType, setSelectedIoType] = useState('1');
    const [presentIoTypes, setPresentIoTypes] = useState(ioTypes);
    const [isNewTank, setIsNewTank] = useState(false);
    const match = useRouteMatch();
    const history = useHistory();

    useEffect(() => {
        getTank();
    }, []);

    useEffect(() => {
        updateIoTypes();
    }, [item]);

    const getTank = async () => {
        const tankId = match.params.id ? match.params.id : 0;
        try {
            appService.setLoading(true);
            const tank = await tankService.getTankAsync(tankId);
            const isNew = tankId === 0;
            if (isNew) {
                let areaId = item.areaId;
                if (match.params.areaId) {
                    areaId = parseInt(match.params.areaId);
                }
                tank.areaId = areaId;
            }

            setIsNewTank(isNew);
            setItem(tank);
            appService.setLoading(false);
        }
        catch (error) {
            appService.setLoading(false);
        }
    }

    const copyTankState = () => {
        let copyItem = { ...item, 
            device: { 
                ...item.device, 
                deviceIoSettings: { ...item.device.deviceIoSettings }, 
                deviceExtendedIos: utils.toNewArray(item.device.deviceExtendedIos)
            } 
        };

        return copyItem;
    }

    const updateIoTypes = () => {
        const deviceExtendedIos = item.device.deviceExtendedIos;

        const presIoTypes = ioTypes.filter((item) => {
            return (deviceExtendedIos.find((d) => d.ioTypeId === item.ioTypeId)) ? false : true;
        }).filter((item) => isFieldAccess(item.ioTypeName, 2));

        setPresentIoTypes(presIoTypes);
        if (presIoTypes && presIoTypes.length > 0) {
            setSelectedIoType(presIoTypes[0].ioTypeId);
        }
    }

    const addAdditionalIo = (e) => {
        e.preventDefault();
        const io = {...emptyIo};
        const tank = copyTankState();
        io.ioTypeId = parseInt(selectedIoType);
        io.deviceImei = tank.device.deviceImei;
        tank.device.deviceExtendedIos.push(io);
        setItem(tank);
    }

    const removeIo = (io) => {
        const newItem = copyTankState();
        newItem.device.deviceExtendedIos = newItem.device.deviceExtendedIos.filter((value, index, arr) => value !== io);
        setItem(newItem);
    }

    const updateGraduations = (file) => {
        if (file) {
            setGraduationFile(file);
        }
    }

    const removeGraduations = () => {
        setItem({ ...item, graduationFileId: null });
        setGraduationFile(null);
    }

    const downloadGraduationsFile = () => {
        tankService.getGraduationsFileAsync(item.graduationFileId).then(data => {
            FileSaver.saveAs(data.file, data.fileName);
        });;
    }

    const updateIoData = (io, index) => {
        const newItem = copyTankState();
        newItem.device.deviceExtendedIos = newItem.device.deviceExtendedIos.map((item, i) => i === index ? io : item);
        setItem(newItem);
    }

    const updateDeviceIoSettingsState = (propName, propValue) => {
        const newItem = copyTankState();
        newItem.device.deviceIoSettings[propName] = propValue;
        setItem(newItem);
    }

    const updateTank = (propName, strValue, dataType) => {
        const tank = copyTankState();
        tank[propName] = convertValue(strValue, dataType);
        setItem(tank);
    }

    const updateDevice = (propName, value, dataType) => {
        const tank = copyTankState();
        tank.device[propName] = convertValue(value, dataType);
        setItem(tank);
    }

    const convertValue = (value, dataType) => {
        if (dataType === "int") {
            return parseInt(value);
        }
        if (dataType === "float") {
            return parseFloat(value);
        }

        return value;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isNewTank) {
            await tankService.addTankAsync(item, graduationFile);
            history.replace('/tanks/');
        } else {
            await tankService.editTankAsync(item, graduationFile);
            history.replace('/tanks/tankdetails/' + item.tankId);
        }
    }

    const cancelEditTank = (e) => {
        e.preventDefault();
        history.goBack();
    }

    const getIoTypeTranslatedName = (ioTypeName) => {
        switch (ioTypeName) {
            case "WatterLevelHeight":
                return i18n.t("ExtendedIo.watterLevelHeightIoName");
            case "WatterLevelVolume":
                return i18n.t("ExtendedIo.watterLevelVolumeIoName");
            case "ProductLevelHeight":
                return i18n.t("ExtendedIo.productLevelHeightIoName");
            case "ProducLevelVolume":
                return i18n.t("ExtendedIo.producLevelVolumeIoName");
            case "Temperature":
                return i18n.t("ExtendedIo.temperatureIoName");
            case "Density":
                return i18n.t("ExtendedIo.densityIoName");
            case "Pressure":
                return i18n.t("ExtendedIo.pressureIoName");
            case "Weight":
                return i18n.t("ExtendedIo.weightIoName");
            case "WatterLevelHeightQuality":
                return i18n.t("ExtendedIo.watterLevelHeightIoQualityName");
            case "WatterLevelVolumeQuality":
                return i18n.t("ExtendedIo.watterLevelVolumeIoQualityName");
            case "ProductLevelHeightQuality":
                return i18n.t("ExtendedIo.productLevelHeightIoQualityName");
            case "ProducLevelVolumeQuality":
                return i18n.t("ExtendedIo.producLevelVolumeIoQualityName");
            case "TemperatureQuality":
                return i18n.t("ExtendedIo.temperatureIoQualityName");
            case "DensityQuality":
                return i18n.t("ExtendedIo.densityIoQualityName");
            case "PressureQuality":
                return i18n.t("ExtendedIo.pressureIoQualityName");
            case "WeightQuality":
                return i18n.t("ExtendedIo.weightIoQualityName");

            default:
                return "";
        }
    }

    const getIoTypeTranslatedNameById = (ioId) => {
        const ioTypeName = getIoTypeNameById(ioId);
        return getIoTypeTranslatedName(ioTypeName);
    }

    const getIoTypeNameById = (ioId) => {
        return ioTypes.find((item) => {
            return item.ioTypeId === ioId;
        }).ioTypeName;
    }

    const isFieldAccess = (fieldName, access) => {
        const fieldAccess = fieldsAccess[fieldName];
        if (isNewTank) return true;
        if (fieldAccess && fieldAccess >= access) return true;
        return false;
    }

    ///CHECK THIS


    ////
    return (
        <div className="form_container">
            <form className="edit-tank" onSubmit={handleSubmit}>
                <div className="form-body flex-column-border-content">
                    <div id="tankSettings">
                        <div className="flex margin10">
                            <div>
                                <div className="inline">
                                    <label><Trans>Tanks.areaName</Trans></label>
                                    <select value={item.areaId} onChange={(e) => updateTank("arreaId", e.target.value, "int")}>
                                        {
                                            areas &&
                                            areas.map((item, index) =>
                                            (
                                                <option className="select_option" key={index} value={item.areaId}>
                                                    {item.areaName}</option>
                                            )
                                            )
                                        }
                                    </select>
                                </div>
                                <div className="inline">
                                    <label><Trans>Tanks.tankName</Trans></label>
                                    <input type="text" value={item.tankName} onChange={(e) => updateTank("tankName", e.target.value)} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Tanks.department</Trans></label>
                                    <input type="text" value={item.department} onChange={(e) => updateTank("department", e.target.value)} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Tanks.fuelType</Trans></label>
                                    <input type="text" value={item.fuelType} onChange={(e) => updateTank("fuelType", e.target.value)} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Tanks.fuelNumber</Trans></label>
                                    <input type="text" value={item.fuelNumber} onChange={(e) => updateTank("fuelNumber", e.target.value)} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Tanks.minLevel</Trans></label>
                                    <input type="number" value={item.minLevel} onChange={(e) => updateTank("minLevel", e.target.value, "float")} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Tanks.maxLevel</Trans></label>
                                    <input type="number" value={item.maxLevel} onChange={(e) => updateTank("maxLevel", e.target.value, "float")} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Tanks.volume</Trans></label>
                                    <input type="number" value={item.volume} onChange={(e) => updateTank("volume", e.target.value, "float")} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Tanks.graduations</Trans></label>
                                    <div className="flex">
                                        {
                                            item.graduationFileId || graduationFile ? <div className="check-background-green"></div> : <div className="close-background-red"></div>
                                        }
                                        <label className="file-upload-button" htmlFor="upload-graduations-file">
                                        </label>
                                        <input type="file" id='upload-graduations-file' onChange={(e) => updateGraduations(e.target.files[0])} style={{ display: 'none' }} />
                                        {
                                            item.graduationFileId &&
                                            <button className="app-button-style file-download-button" onClick={(e) => { e.preventDefault(); downloadGraduationsFile() }}></button>
                                        }
                                        {
                                            (item.graduationFileId || graduationFile) && (
                                                <button className="app-button-style file-remove-button" onClick={(e) => { e.preventDefault(); removeGraduations(); }}></button>
                                            )
                                        }
                                    </div>


                                </div>
                            </div>
                            <div>
                                <div className="inline">
                                    <label><Trans>Devices.deviceImei</Trans></label>
                                    <input type="text" value={item.device.deviceImei}
                                        onChange={(e) => updateDevice("deviceImei", e.target.value)} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Devices.name</Trans></label>
                                    <input type="text" value={item.device.name} onChange={(e) => updateDevice("name", e.target.value)} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Devices.description</Trans></label>
                                    <input type="text" value={item.device.description} onChange={(e) => updateDevice("description", e.target.value)} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Devices.deviceType</Trans></label>
                                    <select value={item.device.deviceTypeId} onChange={(e) => updateDevice("deviceTypeId", e.target.value, "int")}>
                                        {
                                            deviceTypes &&
                                            deviceTypes.map((item, index) =>
                                            (
                                                <option className="select_option" key={index} value={item.deviceTypeId}>
                                                    {item.deviceTypeDesc}</option>
                                            )
                                            )
                                        }
                                    </select>
                                </div>
                                <div className="toogle-element inline">
                                    <label><Trans>Devices.isActive</Trans></label>
                                    <ToggleButton value={item.device.isActive} onChange={(checked) => updateDevice("isActive", checked)} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div id="deviceIoSettings">
                        <div className="flex margin10">
                            <div>
                                <div className="inline">
                                    <label><Trans>Devices.deviceIoSettings.volumeCountIoId</Trans></label>
                                    <input type="number" value={item.device.deviceIoSettings.volumeCountIoId}
                                        onChange={(e) => updateDeviceIoSettingsState("volumeCountIoId", e.target.value)} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Devices.deviceIoSettings.volumeRatio</Trans></label>
                                    <input type="number" step="0.01" value={item.device.deviceIoSettings.volumeRatio} onChange={(e) => updateDeviceIoSettingsState("volumeRatio", e.target.value)} />
                                </div>
                            </div>
                            <div>
                                <div className="inline">
                                    <label><Trans>Devices.deviceIoSettings.valveIoId</Trans></label>
                                    <input type="number" value={item.device.deviceIoSettings.valveIoId} onChange={(e) => updateDeviceIoSettingsState("valveIoId", e.target.value)} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Devices.deviceIoSettings.userKeyIoId</Trans></label>
                                    <input type="number" value={item.device.deviceIoSettings.userKeyIoId} onChange={(e) => updateDeviceIoSettingsState("userKeyIoId", e.target.value)} />
                                </div>
                                <div className="inline">
                                    <label><Trans>Devices.deviceIoSettings.autoKeyIoId</Trans></label>
                                    <input type="number" value={item.device.deviceIoSettings.avtoKeyIoId} onChange={(e) => updateDeviceIoSettingsState("avtoKeyIoId", e.target.value)} />
                                </div>
                            </div>
                        </div>

                    </div>
                    <div id="deviceExtendedIoSettings" className="no-flex-border">
                        {
                            isFieldAccess(AppFields.EditExtendedIoList, AccessLevel.Write) && presentIoTypes.length > 0 &&
                            <div >
                                <div className="no-flex-border margin10 marginTop30" style={{ fontSize: "18px" }}>
                                    <Trans>ExtendedIo.EditTankHeader</Trans>
                                </div>
                            </div>
                        }

                        <div className="flex-column-border-content">
                            {
                                item.device.deviceExtendedIos && item.device.deviceExtendedIos.filter(
                                    i => {
                                        return isFieldAccess(getIoTypeNameById(i.ioTypeId), AccessLevel.Write);
                                    }).map(
                                        (item, index) => {
                                            const ioIndex = index;
                                            return <div key={index} style={{ position: "relative" }}>
                                                <ExtendedIo key={index}
                                                    ioTypeName={getIoTypeNameById(item.ioTypeId)}
                                                    ioDataTypes={["int", "float", "string"]}
                                                    ioActionTypes={ioActionTypes}
                                                    io={item}
                                                    canBeEdit={isFieldAccess(AppFields.EditExtendedIoList, AccessLevel.Write)}
                                                    changed={(item) => updateIoData(item, ioIndex)}
                                                    removeIoAction={removeIo} />
                                            </div>;
                                        }
                                    )
                            }
                            {isFieldAccess(AppFields.EditExtendedIoList, AccessLevel.Write) && presentIoTypes && presentIoTypes.length > 0 && <div className="inline marginTop30 no-flex-border">
                                <label><Trans>ExtendedIo.ioType</Trans></label>
                                <select onChange={(e) => setSelectedIoType(e.target.value)} value={selectedIoType}>
                                    {
                                        presentIoTypes.map((item, index) =>
                                            <option className="select_option" key={index} value={item.ioTypeId}>
                                                {getIoTypeTranslatedName(item.ioTypeName)}</option>
                                        )
                                    }
                                </select>
                                <div style={{ position: "relative", height: "31px", width: "60px", marginLeft: "10px" }}>
                                    <button className={styles.add_item_button} style={{ position: "unset" }} onClick={addAdditionalIo}></button>
                                </div>
                            </div>
                            }
                        </div>
                    </div>
                </div>
                <div className="form-buttons-container">
                    <button type="submit"><Trans>Table.saveButton</Trans></button>
                    <button onClick={cancelEditTank}><Trans>Table.cancelButton</Trans></button>
                </div>
            </form>
        </div>
    );
}


export default withRouter(connect(mapStateToProps)(withTranslation('translantions')(EditTank)));